import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Search, X, TrendingUp, Briefcase, Wallet, Users, Settings, Shield,
  GraduationCap, Newspaper, BarChart3, Phone, FileText, Award, Trophy,
  Gift, Home, CornerDownLeft, Flame, Sparkles, Clock, ArrowRight, BookOpen,
  Coins, Globe2,
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import { ARTICLES, CATEGORIES as ART_CATEGORIES } from '../../data/articles';

const API = process.env.REACT_APP_BACKEND_URL;
const RECENT_KEY = 'varotrades_recent_searches';

const buildRouteIndex = (isRTL) => [
  { icon: Home, ar: 'الرئيسية', en: 'Home', path: '/', category: { ar: 'الموقع', en: 'Site' } },
  { icon: Briefcase, ar: 'الحسابات', en: 'Accounts', path: '/accounts', category: { ar: 'الموقع', en: 'Site' } },
  { icon: Briefcase, ar: 'حساب Pro ECN', en: 'Pro ECN Account', path: '/accounts/pro-ecn', category: { ar: 'الحسابات', en: 'Accounts' } },
  { icon: Briefcase, ar: 'حساب Raw ECN', en: 'Raw ECN Account', path: '/accounts/raw-ecn', category: { ar: 'الحسابات', en: 'Accounts' } },
  { icon: Briefcase, ar: 'حساب Standard STP', en: 'Standard STP Account', path: '/accounts/standard', category: { ar: 'الحسابات', en: 'Accounts' } },
  { icon: Briefcase, ar: 'حساب Swap Free', en: 'Swap-Free Account', path: '/accounts/swap-free', category: { ar: 'الحسابات', en: 'Accounts' } },
  { icon: BarChart3, ar: 'الأسواق', en: 'Markets', path: '/markets-hub', category: { ar: 'الأسواق', en: 'Markets' } },
  { icon: TrendingUp, ar: 'الفوركس', en: 'Forex', path: '/markets/forex', category: { ar: 'الأسواق', en: 'Markets' } },
  { icon: TrendingUp, ar: 'العملات الرقمية', en: 'Crypto', path: '/markets', category: { ar: 'الأسواق', en: 'Markets' } },
  { icon: TrendingUp, ar: 'المؤشرات', en: 'Indices', path: '/markets/indices', category: { ar: 'الأسواق', en: 'Markets' } },
  { icon: TrendingUp, ar: 'السلع', en: 'Commodities', path: '/markets/commodities', category: { ar: 'الأسواق', en: 'Markets' } },
  { icon: TrendingUp, ar: 'الأسهم', en: 'Shares', path: '/markets/shares', category: { ar: 'الأسواق', en: 'Markets' } },
  { icon: BarChart3, ar: 'المنصات', en: 'Platforms', path: '/platforms', category: { ar: 'المنصات', en: 'Platforms' } },
  { icon: BarChart3, ar: 'منصة VAROTRADES', en: 'VAROTRADES App', path: '/platforms/app', category: { ar: 'المنصات', en: 'Platforms' } },
  { icon: BarChart3, ar: 'تداول الويب', en: 'Web Trading', path: '/platforms/web', category: { ar: 'المنصات', en: 'Platforms' } },
  { icon: BarChart3, ar: 'MetaTrader 4', en: 'MetaTrader 4', path: '/platforms/mt4', category: { ar: 'المنصات', en: 'Platforms' } },
  { icon: BarChart3, ar: 'MetaTrader 5', en: 'MetaTrader 5', path: '/platforms/mt5', category: { ar: 'المنصات', en: 'Platforms' } },
  { icon: BarChart3, ar: 'TradingView', en: 'TradingView', path: '/platforms/tradingview', category: { ar: 'المنصات', en: 'Platforms' } },
  { icon: Wallet, ar: 'الإيداع', en: 'Deposit', path: '/funds?tab=deposit', category: { ar: 'الأموال', en: 'Funds' } },
  { icon: Wallet, ar: 'السحب', en: 'Withdrawal', path: '/funds?tab=withdrawal', category: { ar: 'الأموال', en: 'Funds' } },
  { icon: FileText, ar: 'الرسوم والفروقات', en: 'Spreads', path: '/fees/spreads', category: { ar: 'الرسوم', en: 'Fees' } },
  { icon: FileText, ar: 'العمولات', en: 'Commissions', path: '/fees/commissions', category: { ar: 'الرسوم', en: 'Fees' } },
  { icon: FileText, ar: 'حاسبة التداول', en: 'Trading Calculator', path: '/fees/calculator', category: { ar: 'الرسوم', en: 'Fees' } },
  { icon: Users, ar: 'نسخ التداول', en: 'Copy Trading', path: '/copy-trading', category: { ar: 'منتجات', en: 'Products' } },
  { icon: GraduationCap, ar: 'الأكاديمية', en: 'Academy', path: '/learn', category: { ar: 'التعلّم', en: 'Learn' } },
  { icon: GraduationCap, ar: 'المقالات', en: 'Articles', path: '/learn/articles', category: { ar: 'التعلّم', en: 'Learn' } },
  { icon: GraduationCap, ar: 'الدورات', en: 'Courses', path: '/learn/courses', category: { ar: 'التعلّم', en: 'Learn' } },
  { icon: GraduationCap, ar: 'الكتب الإلكترونية', en: 'eBooks', path: '/learn/ebooks', category: { ar: 'التعلّم', en: 'Learn' } },
  { icon: GraduationCap, ar: 'القاموس', en: 'Glossary', path: '/learn/glossary', category: { ar: 'التعلّم', en: 'Learn' } },
  { icon: Newspaper, ar: 'التقويم الاقتصادي', en: 'Economic Calendar', path: '/analysis/calendar', category: { ar: 'التحليلات', en: 'Analysis' } },
  { icon: Newspaper, ar: 'إشارات التداول', en: 'Trading Signals', path: '/analysis/signals', category: { ar: 'التحليلات', en: 'Analysis' } },
  { icon: Award, ar: 'برنامج IB', en: 'IB Program', path: '/partners/ib', category: { ar: 'الشراكات', en: 'Partners' } },
  { icon: Trophy, ar: 'العروض الترويجية', en: 'Promotions', path: '/promotions', category: { ar: 'العروض', en: 'Promotions' } },
  { icon: Gift, ar: 'بونص الترحيب', en: 'Welcome Bonus', path: '/promotions/welcome-bonus', category: { ar: 'العروض', en: 'Promotions' } },
  { icon: Shield, ar: 'لماذا VAROTRADES', en: 'Why VAROTRADES', path: '/about/why', category: { ar: 'حول', en: 'About' } },
  { icon: Shield, ar: 'التراخيص والتنظيم', en: 'Regulation', path: '/about/regulation', category: { ar: 'حول', en: 'About' } },
  { icon: Phone, ar: 'تواصل معنا', en: 'Contact', path: '/about/contact', category: { ar: 'حول', en: 'About' } },
];

const QUICK_CATEGORIES = [
  { ar: 'الأسواق', en: 'Markets',    path: '/markets-hub',     icon: BarChart3,  color: 'from-orange-500 to-rose-500' },
  { ar: 'الحسابات', en: 'Accounts',  path: '/accounts',        icon: Briefcase,  color: 'from-amber-500 to-orange-500' },
  { ar: 'المنصات', en: 'Platforms',  path: '/platforms',       icon: Globe2,     color: 'from-violet-500 to-indigo-500' },
  { ar: 'الأكاديمية', en: 'Academy', path: '/learn',           icon: GraduationCap, color: 'from-emerald-500 to-teal-500' },
  { ar: 'العروض', en: 'Promotions',  path: '/promotions',      icon: Gift,       color: 'from-pink-500 to-rose-500' },
  { ar: 'الأموال', en: 'Funds',      path: '/funds?tab=deposit', icon: Wallet,   color: 'from-sky-500 to-blue-500' },
];

const POPULAR_QUERIES = {
  ar: ['XAU/USD', 'فهم النقاط', 'صناديق ETF', 'الفيدرالي', 'حاسبة', 'إيداع'],
  en: ['XAU/USD', 'Pips', 'Bitcoin ETFs', 'Fed decisions', 'Calculator', 'Deposit'],
};

const GlobalSearch = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { isRTL } = useLanguage();
  const lang = isRTL ? 'ar' : 'en';
  const [q, setQ] = useState('');
  const [assets, setAssets] = useState([]);
  const [active, setActive] = useState(0);
  const [recent, setRecent] = useState([]);
  const inputRef = useRef(null);

  const routes = useMemo(() => buildRouteIndex(isRTL), [isRTL]);

  // Lock background page scroll while the search modal is open.
  useBodyScrollLock(open);

  useEffect(() => {
    if (!open) return;
    setTimeout(() => inputRef.current?.focus(), 60);
    if (assets.length === 0) {
      axios.get(`${API}/api/assets`).then((r) => setAssets(r.data || [])).catch(() => {});
    }
    try {
      const stored = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      setRecent(stored.slice(0, 5));
    } catch { /* ignore */ }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setActive(0); }, [q]);

  const { routeHits, assetHits, articleHits } = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return { routeHits: [], assetHits: [], articleHits: [] };
    const rh = routes.filter((r) =>
      (r.ar || '').toLowerCase().includes(query) ||
      (r.en || '').toLowerCase().includes(query) ||
      (r.category?.ar || '').toLowerCase().includes(query) ||
      (r.category?.en || '').toLowerCase().includes(query) ||
      (r.path || '').toLowerCase().includes(query)
    ).slice(0, 6);
    const ah = assets.filter((a) =>
      (a.symbol || '').toLowerCase().includes(query) ||
      (a.name || '').toLowerCase().includes(query)
    ).slice(0, 5);
    const arth = ARTICLES.filter((a) =>
      a.title[lang].toLowerCase().includes(query) ||
      a.excerpt[lang].toLowerCase().includes(query) ||
      a.cat.toLowerCase().includes(query)
    ).slice(0, 4);
    return { routeHits: rh, assetHits: ah, articleHits: arth };
  }, [q, routes, assets, lang]);

  const flat = useMemo(
    () => [
      ...routeHits.map((r) => ({ kind: 'route', ...r })),
      ...articleHits.map((a) => ({ kind: 'article', ...a })),
      ...assetHits.map((a) => ({ kind: 'asset', ...a })),
    ],
    [routeHits, assetHits, articleHits]
  );

  const pushRecent = (label) => {
    try {
      const updated = [label, ...recent.filter((r) => r !== label)].slice(0, 5);
      localStorage.setItem(RECENT_KEY, JSON.stringify(updated));
      setRecent(updated);
    } catch { /* ignore */ }
  };

  const go = (item) => {
    if (item.kind === 'article') {
      pushRecent(item.title[lang]);
      navigate(`/learn/articles/${item.slug}`);
    } else if (item.kind === 'asset') {
      const cat = item.category || 'forex';
      const map = { forex: '/markets/forex', stocks: '/markets/shares', indices: '/markets/indices', commodities: '/markets/commodities', etfs: '/markets/etfs' };
      pushRecent(item.symbol);
      navigate(map[cat] || '/markets');
    } else {
      pushRecent(isRTL ? item.ar : item.en);
      navigate(item.path);
    }
    onClose();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, flat.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === 'Enter')     { e.preventDefault(); const item = flat[active]; if (item) go(item); }
  };

  const hasQuery = q.trim().length > 0;
  const noResults = hasQuery && routeHits.length === 0 && assetHits.length === 0 && articleHits.length === 0;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[200] bg-ink-900/50 backdrop-blur-md flex items-start justify-center px-3 pt-[6vh]"
          onClick={onClose}
          dir={isRTL ? 'rtl' : 'ltr'}
          data-testid="global-search-overlay"
        >
          <motion.div
            initial={{ y: 20, opacity: 0, scale: 0.97 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 12, opacity: 0, scale: 0.98 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className="bg-white w-full max-w-3xl rounded-3xl overflow-hidden shadow-2xl border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            {/* === HEADER WITH GRADIENT === */}
            <div className="relative bg-gradient-to-br from-flame-500 via-flame-600 to-orange-700 px-6 pt-5 pb-6 overflow-hidden">
              {/* decorative blurs */}
              <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-yellow-300/20 blur-3xl" />
              <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-rose-300/20 blur-3xl" />
              <div className="relative">
                <div className={`flex items-center gap-2 mb-3 text-white/90 text-xs font-bold uppercase tracking-[0.18em] ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Sparkles className="w-3.5 h-3.5" />
                  {isRTL ? 'بحث ذكي عبر الموقع' : 'Smart Site Search'}
                </div>
                <div className={`flex items-center gap-3 bg-white/95 backdrop-blur rounded-2xl px-4 py-3 shadow-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                  <Search className="w-5 h-5 text-flame-500 flex-shrink-0" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={isRTL ? 'ابحث عن صفحة، مقال، أو زوج عملات…' : 'Search pages, articles, or instruments…'}
                    className={`flex-1 bg-transparent text-base outline-none placeholder:text-ink-400 text-ink-900 ${isRTL ? 'text-right' : ''}`}
                    data-testid="global-search-input"
                  />
                  {q && (
                    <button onClick={() => setQ('')} className="p-1 rounded-md hover:bg-flame-50" data-testid="global-search-clear">
                      <X className="w-4 h-4 text-ink-400" />
                    </button>
                  )}
                  <kbd className="hidden md:inline-flex h-6 px-2 items-center font-mono text-[10px] bg-flame-50 border border-flame-200 text-flame-700 rounded font-bold">ESC</kbd>
                </div>
              </div>
            </div>

            {/* === BODY === */}
            <div className="max-h-[68vh] overflow-y-auto p-4">

              {/* === HERO STATE (no query) === */}
              {!hasQuery && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.05 }}
                  className="space-y-6"
                >
                  {/* Quick Category Tiles */}
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] font-black text-ink-500 mb-3 px-2">
                      {isRTL ? 'استكشف بسرعة' : 'Quick explore'}
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                      {QUICK_CATEGORIES.map((c, i) => (
                        <motion.button
                          key={c.path}
                          initial={{ y: 10, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{ delay: 0.04 * i }}
                          onClick={() => { navigate(c.path); onClose(); }}
                          className="group relative aspect-square rounded-2xl overflow-hidden border border-flame-100 hover:border-flame-300 hover:-translate-y-0.5 transition-all"
                          data-testid={`quick-cat-${c.en}`}
                        >
                          <div className={`absolute inset-0 bg-gradient-to-br ${c.color} opacity-90`} />
                          <div className="absolute inset-0 bg-ink-900/10 group-hover:bg-transparent transition-colors" />
                          <div className="relative h-full flex flex-col items-center justify-center text-white p-2">
                            <c.icon className="w-6 h-6 mb-1.5 drop-shadow" />
                            <span className="text-[11px] font-black drop-shadow">{c[lang]}</span>
                          </div>
                        </motion.button>
                      ))}
                    </div>
                  </div>

                  {/* Recent Searches */}
                  {recent.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] font-black text-ink-500 mb-2 px-2 flex items-center gap-1.5">
                        <Clock className="w-3 h-3" />
                        {isRTL ? 'بحوثاتك الأخيرة' : 'Recent searches'}
                      </div>
                      <div className="flex flex-wrap gap-2 px-1">
                        {recent.map((r) => (
                          <button
                            key={r}
                            onClick={() => setQ(r)}
                            className="px-3 h-8 rounded-full bg-flame-50/70 hover:bg-flame-100 text-ink-700 text-xs font-bold transition-all"
                            data-testid={`recent-${r}`}
                          >
                            {r}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Popular Searches */}
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] font-black text-ink-500 mb-2 px-2 flex items-center gap-1.5">
                      <Flame className="w-3 h-3 text-flame-500" />
                      {isRTL ? 'الأكثر بحثاً' : 'Popular searches'}
                    </div>
                    <div className="flex flex-wrap gap-2 px-1">
                      {POPULAR_QUERIES[lang].map((p) => (
                        <button
                          key={p}
                          onClick={() => setQ(p)}
                          className="px-3 h-8 rounded-full bg-gradient-to-br from-flame-500 to-flame-600 hover:from-flame-600 hover:to-flame-700 text-white text-xs font-bold transition-all shadow-sm"
                          data-testid={`popular-${p}`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Top Articles preview */}
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] font-black text-ink-500 mb-2 px-2 flex items-center gap-1.5">
                      <BookOpen className="w-3 h-3 text-flame-500" />
                      {isRTL ? 'مقالات مميزة' : 'Featured articles'}
                    </div>
                    <div className="space-y-1.5">
                      {ARTICLES.slice(0, 3).map((a, i) => (
                        <motion.button
                          key={a.id}
                          initial={{ x: isRTL ? 10 : -10, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          transition={{ delay: 0.04 * i + 0.1 }}
                          onClick={() => go({ kind: 'article', ...a })}
                          className={`w-full group flex items-center gap-3 p-2 rounded-xl hover:bg-flame-50/60 transition-colors ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}
                          data-testid={`featured-article-${a.slug}`}
                        >
                          <img src={a.cover} alt="" loading="lazy" className="w-14 h-14 rounded-lg object-cover flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-flame-600 font-black uppercase tracking-wider mb-0.5">
                              {ART_CATEGORIES.find((c) => c.id === a.cat)?.[lang] || a.cat}
                            </div>
                            <div className="text-sm font-bold text-ink-900 line-clamp-1">{a.title[lang]}</div>
                          </div>
                          <ArrowRight className={`w-4 h-4 text-ink-300 group-hover:text-flame-500 group-hover:translate-x-1 transition-all flex-shrink-0 ${isRTL ? 'rotate-180' : ''}`} />
                        </motion.button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* === RESULTS STATE === */}
              {hasQuery && (
                <div className="space-y-4">
                  {/* Routes */}
                  {routeHits.length > 0 && (
                    <Section title={isRTL ? 'صفحات' : 'Pages'} icon={Globe2}>
                      {routeHits.map((r, i) => (
                        <ResultRow
                          key={`r-${r.path}-${i}`}
                          isActive={active === i}
                          onHover={() => setActive(i)}
                          onClick={() => go({ kind: 'route', ...r })}
                          isRTL={isRTL}
                          icon={<r.icon className="w-4 h-4" />}
                          title={r[lang]}
                          subtitle={`${r.category[lang]} · ${r.path}`}
                          testId={`global-search-route-${i}`}
                        />
                      ))}
                    </Section>
                  )}

                  {/* Articles */}
                  {articleHits.length > 0 && (
                    <Section title={isRTL ? 'مقالات' : 'Articles'} icon={BookOpen}>
                      {articleHits.map((a, i) => {
                        const idx = routeHits.length + i;
                        return (
                          <button
                            key={`art-${a.id}`}
                            onClick={() => go({ kind: 'article', ...a })}
                            onMouseEnter={() => setActive(idx)}
                            className={`w-full group flex items-center gap-3 p-2 rounded-xl transition-colors ${active === idx ? 'bg-flame-50' : 'hover:bg-flame-50/60'} ${isRTL ? 'flex-row-reverse text-right' : 'text-left'}`}
                            data-testid={`global-search-article-${i}`}
                          >
                            <img src={a.cover} alt="" loading="lazy" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="text-[10px] text-flame-600 font-black uppercase tracking-wider mb-0.5">
                                {ART_CATEGORIES.find((c) => c.id === a.cat)?.[lang] || a.cat} · {a.read} {isRTL ? 'دقيقة' : 'min'}
                              </div>
                              <div className="text-sm font-bold text-ink-900 line-clamp-1">{a.title[lang]}</div>
                            </div>
                            {active === idx && <CornerDownLeft className="w-3.5 h-3.5 text-flame-500 flex-shrink-0" />}
                          </button>
                        );
                      })}
                    </Section>
                  )}

                  {/* Live Instruments */}
                  {assetHits.length > 0 && (
                    <Section title={isRTL ? 'أدوات مباشرة' : 'Live instruments'} icon={Coins}>
                      {assetHits.map((a, i) => {
                        const idx = routeHits.length + articleHits.length + i;
                        const up = (a.change_24h || 0) >= 0;
                        return (
                          <button
                            key={`a-${a.symbol}`}
                            onClick={() => go({ kind: 'asset', ...a })}
                            onMouseEnter={() => setActive(idx)}
                            className={`w-full flex items-center gap-3 p-2 rounded-xl transition-colors ${active === idx ? 'bg-flame-50' : 'hover:bg-flame-50/60'} ${isRTL ? 'flex-row-reverse' : ''}`}
                            data-testid={`global-search-asset-${i}`}
                          >
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-mono font-bold text-[10px] ${active === idx ? 'bg-flame-500 text-white' : 'bg-flame-50 text-flame-700'}`}>
                              {a.symbol.slice(0, 3)}
                            </div>
                            <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : 'text-left'}`}>
                              <div className="text-sm font-bold text-ink-900">{a.symbol} <span className="text-ink-500 font-normal">· {a.name}</span></div>
                              <div className="text-[10.5px] text-ink-500">{a.category}</div>
                            </div>
                            <div className={isRTL ? 'text-left' : 'text-right'}>
                              <div className="font-mono text-sm font-bold tabular-nums text-ink-900">{Number(a.bid || 0).toFixed(a.bid < 10 ? 4 : 2)}</div>
                              <div className={`text-[10.5px] font-bold ${up ? 'text-emerald-600' : 'text-red-600'}`}>{up ? '+' : ''}{Number(a.change_24h || 0).toFixed(2)}%</div>
                            </div>
                          </button>
                        );
                      })}
                    </Section>
                  )}

                  {/* Empty State */}
                  {noResults && (
                    <div className="text-center py-12" data-testid="global-search-empty">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-flame-50 flex items-center justify-center">
                        <Search className="w-7 h-7 text-flame-400" />
                      </div>
                      <div className="text-base font-black text-ink-900 mb-1">{isRTL ? `لا توجد نتائج لـ "${q}"` : `No results for "${q}"`}</div>
                      <div className="text-sm text-ink-500">{isRTL ? 'جرّب كلمات مختلفة أو تصفّح الأقسام أعلاه' : 'Try different words or browse the sections above'}</div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* === FOOTER === */}
            <div className={`px-5 py-3 border-t border-flame-100 bg-flame-50/40 flex items-center gap-4 text-[10.5px] text-ink-500 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <span className={`flex items-center gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}><kbd className="font-mono font-bold bg-white border border-flame-200 rounded px-1.5">↑↓</kbd> {isRTL ? 'تنقّل' : 'navigate'}</span>
              <span className={`flex items-center gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}><kbd className="font-mono font-bold bg-white border border-flame-200 rounded px-1.5">↵</kbd> {isRTL ? 'فتح' : 'open'}</span>
              <span className={`flex items-center gap-1.5 ${isRTL ? 'flex-row-reverse' : ''}`}><kbd className="font-mono font-bold bg-white border border-flame-200 rounded px-1.5">ESC</kbd> {isRTL ? 'إغلاق' : 'close'}</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Section = ({ title, icon: Icon, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.18em] font-black text-ink-500 mb-2 px-2 flex items-center gap-1.5">
      <Icon className="w-3 h-3 text-flame-500" />
      {title}
    </div>
    <div className="space-y-0.5">{children}</div>
  </div>
);

const ResultRow = ({ isActive, onHover, onClick, isRTL, icon, title, subtitle, testId }) => (
  <button
    onClick={onClick}
    onMouseEnter={onHover}
    className={`w-full flex items-center gap-3 px-2 py-2 rounded-xl transition-colors ${isActive ? 'bg-flame-50' : 'hover:bg-flame-50/60'} ${isRTL ? 'flex-row-reverse' : ''}`}
    data-testid={testId}
  >
    <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-flame-500 text-white' : 'bg-flame-50 text-flame-700'}`}>
      {icon}
    </div>
    <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : 'text-left'}`}>
      <div className="text-sm font-bold text-ink-900 truncate">{title}</div>
      <div className="text-[11px] text-ink-500 truncate">{subtitle}</div>
    </div>
    {isActive && <CornerDownLeft className={`w-3.5 h-3.5 text-flame-500 flex-shrink-0 ${isRTL ? 'scale-x-[-1]' : ''}`} />}
  </button>
);

export default GlobalSearch;
